// src/pages/ChatDocuments/uploadQueue.ts
import api from "../api/axios";
import {
  aesGcmEncryptFile,
  requireMySecret,
  requireRecipientKey,
} from "./chatAsync";
import { encryptMessage, publicKeyFromSecret } from "../crypto/simpleE2EE";

export type QueueItem = {
  id: string;             // temporary id used by UI
  toUserId: string;
  file: File;
  ttlAfterViewSec: number;
};

type QueueBatch = {
  batchId: string;
  toUserId: string;
  ttlAfterViewSec: number;
  items: QueueItem[];
  bundleKey: string; 
};

type Progress =
  | { id: string; state: "queued" | "encrypting" | "uploading" | "sent" | "error"; errorMsg?: string; serverId?: string }
  // Optional batch-level progress messages if you want to reflect album status in the UI:
  | { id: string; state: "album-sending" | "album-sent" | "album-error"; batchId: string; errorMsg?: string };
    
class UploadQueue {
  private batches: QueueBatch[] = [];
  private running = false;
  private listeners = new Set<(p: Progress) => void>();

  subscribe(fn: (p: Progress) => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit(p: Progress) {
    this.listeners.forEach((fn) => fn(p));
  }

  enqueue(items: QueueItem[]) {
    if (!items.length) return [];
    const toUserId = items[0].toUserId;
    const ttlAfterViewSec = items[0].ttlAfterViewSec;
    if (!items.every(i => i.toUserId === toUserId)) {
      throw new Error("All items in a batch must target the same receiver");
    }
    if (!items.every(i => i.ttlAfterViewSec === ttlAfterViewSec)) {
      throw new Error("All items in a batch must share the same TTL");
    }
  
    const batchId = (crypto as any)?.randomUUID?.() || `batch_${Date.now()}_${Math.random()}`;
  
    // 👇 one stable bundleKey for this album
    const bundleKey =
      `chat:${toUserId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  
    const batch: QueueBatch = { batchId, toUserId, ttlAfterViewSec, items, bundleKey };
  
    items.forEach(b => this.emit({ id: b.id, state: "queued" }));
    this.batches.push(batch);
    this.kick();
    return { batchId, tempIds: items.map(b => b.id) };
  }
  

  private async kick() {
    if (this.running) return;
    this.running = true;
  
    while (this.batches.length) {
      const job = this.batches.shift()!;
      const serverIds: string[] = [];
  
      try {
        // 🔑 get keys (all async)
        const mySecret = await requireMySecret();          // ⬅️ await here
        const myPub    = publicKeyFromSecret(mySecret);
        const recipientPub = await requireRecipientKey(job.toUserId);
  
        // Upload each item in the batch
        for (const it of job.items) {
          try {
            this.emit({ id: it.id, state: "encrypting" });
            const { cipherBlob, ivB64u, keyB64u } = await aesGcmEncryptFile(it.file);
  
            const mediaKeyEnvelope     = encryptMessage(keyB64u, recipientPub, mySecret);
            const mediaKeySelfEnvelope = encryptMessage(keyB64u, myPub,        mySecret);
  
            const encFile = new File(
              [cipherBlob],
              `${it.file.name || `media_${Date.now()}`}.bin`,
              { type: "application/octet-stream" }
            );
  
            const fd = new FormData();
            fd.append("receiverId", job.toUserId);
            fd.append("media", encFile);
            fd.append("mediaIv", ivB64u);
            fd.append("mediaKeyEnvelope", JSON.stringify(mediaKeyEnvelope));
            fd.append("mediaKeySelfEnvelope", JSON.stringify(mediaKeySelfEnvelope));
            fd.append("origMime", it.file.type || "application/octet-stream");
            fd.append("ttlAfterViewSec", String(job.ttlAfterViewSec));
            fd.append("bundleKey", job.bundleKey);

            this.emit({ id: it.id, state: "uploading" });
  
            const res = await api.post("/message/send", fd);
  
            const serverId = String(res?.data?._id || "");
            if (serverId) serverIds.push(serverId);
            this.emit({ id: it.id, state: "sent", serverId });
          } catch (e: any) {
            this.emit({ id: it.id, state: "error", errorMsg: e?.message || "Failed" });
            // continue with the rest of the batch
          }
        }
  
        // Post album manifest (only if we have at least one success)
        if (serverIds.length) {
          try {
            this.emit({ id: job.batchId, state: "album-sending", batchId: job.batchId });
  
            const manifest     = JSON.stringify({ kind: "album", ids: serverIds });
            const encrypted    = encryptMessage(manifest, recipientPub, mySecret);
            const selfEnvelope = encryptMessage(manifest, myPub,        mySecret);
  
            await api.post(
              "/message/send",
              { receiverId: job.toUserId, encrypted, selfEnvelope, bundleKey: job.bundleKey }
            );
            
  
            this.emit({ id: job.batchId, state: "album-sent", batchId: job.batchId });
          } catch (e: any) {
            this.emit({
              id: job.batchId,
              state: "album-error",
              batchId: job.batchId,
              errorMsg: e?.message || "Failed to post album",
            });
          }
        }
      } catch (e) {
        this.emit({
          id: job.batchId,
          state: "album-error",
          batchId: job.batchId,
          errorMsg: (e as any)?.message || "Batch failed",
        });
      }
    }
  
    this.running = false;
  }
  }

export const uploadQueue = new UploadQueue();
